import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Baby, Heart, Activity, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const MODES = [
  { id: "standard", label: "Standard", icon: Activity, desc: "Normal cycle tracking" },
  { id: "ttc", label: "Trying to Conceive", icon: Heart, desc: "Highlight fertile window + ovulation" },
  { id: "pregnancy", label: "Pregnancy", icon: Baby, desc: "Week-by-week pregnancy tracking" },
];

const WellnessModeSection = () => {
  const { token } = useAuth();
  const [mode, setMode] = useState("standard");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/wellness/mode`, { headers, withCredentials: true })
      .then(res => {
        setMode(res.data.mode || "standard");
        setDueDate(res.data.pregnancy_due_date || "");
      })
      .catch(() => { /* keep defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async (nextMode) => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ mode: nextMode });
      if (nextMode === "pregnancy" && dueDate) params.append("pregnancy_due_date", dueDate);
      await axios.post(`${API}/wellness/mode?${params.toString()}`, null, { headers, withCredentials: true });
      setMode(nextMode);
      toast.success(`Switched to ${MODES.find(m => m.id === nextMode)?.label} mode`);
    } catch {
      toast.error("Could not update mode");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}>
      <Card className="glass" data-testid="wellness-mode-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-mystic-300" />
            Wellness Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-mystic-200/70 text-sm">
            Adapt the app to what you need right now.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {MODES.map(m => {
              const active = mode === m.id;
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => save(m.id)}
                  disabled={saving}
                  data-testid={`mode-${m.id}-btn`}
                  className={`text-left rounded-xl p-4 border transition-all ${
                    active
                      ? "border-mystic-300 bg-mystic-500/15 shadow-[0_0_15px_rgba(157,78,221,0.2)]"
                      : "border-white/10 bg-mystic-900/40 hover:border-mystic-400/40"
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${active ? "text-mystic-200" : "text-mystic-400"}`} />
                  <p className={`text-sm font-medium ${active ? "text-white" : "text-mystic-100"}`}>{m.label}</p>
                  <p className="text-xs text-mystic-200/60 mt-1 leading-snug">{m.desc}</p>
                </button>
              );
            })}
          </div>

          {mode === "pregnancy" && (
            <div className="pt-2 space-y-2">
              <Label className="text-mystic-200">Expected Due Date</Label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="flex-1 bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
                  data-testid="pregnancy-due-date-input"
                />
                <Button
                  onClick={() => save("pregnancy")}
                  className="bg-mystic-600 hover:bg-mystic-500 text-white rounded-full"
                  data-testid="save-due-date-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WellnessModeSection;
